import {
  computed,
  nextTick,
  reactive,
  ref,
  watch,
  onScopeDispose,
  type Ref,
  type Component,
  type ComponentPublicInstance,
} from "vue";
import {
  registerDockWindow,
  unregisterDockWindow,
  setDockWindowMinimized,
  raiseDockWindow,
  visibleWindowCount,
  dockWindowZIndex,
  needsDockWindowBaseZ,
  setDockWindowBaseZ,
} from "@/composables/useDialogWindowDock";

/**
 * Macht einen Vuetify-`v-dialog` zu einem verschieb- und minimierbaren Fenster.
 *
 * Verwendung (Options API, im `setup()` des Dialogs):
 *
 *   setup(props) {
 *     const win = useDialogWindow({
 *       id: "werte-edit",
 *       title: "Werte bearbeiten",
 *       icon: Scale,
 *       isOpen: toRef(props, "modelValue"),
 *     });
 *     return { win };
 *   }
 *
 * Template:
 *   <v-dialog v-bind="win.dialogProps" ...>
 *     <v-card :ref="win.setCardEl" ...>
 *       <header class="dialog-window-handle"
 *               @pointerdown="win.onHandlePointerDown"
 *               @dblclick="win.onHandleDblClick">
 *         ... <button @click="win.minimize">…</button>
 *
 * - Drag läuft rAF-gedrosselt über `transform: translate3d` (Compositor-only).
 * - Position wird ans Viewport geklemmt, der Header bleibt immer erreichbar.
 * - Doppelklick auf den Header zentriert das Fenster wieder.
 * - Minimieren parkt das Fenster als Chip in der MinimizedWindowDock-Leiste;
 *   Scrim, Scroll-Sperre und Fokus-Falle werden dabei freigegeben, sodass die
 *   Seite dahinter voll bedienbar ist. Formularzustand bleibt erhalten.
 * - Jedes offene Fenster registriert sich in der Dock-Leiste (Taskbar-Tab).
 *   Sind mehrere Fenster gleichzeitig sichtbar, schalten alle in den
 *   Fenstermodus (kein Scrim, keine Scroll-Sperre/Fokus-Falle) und lassen
 *   sich frei anordnen; Pointerdown holt ein Fenster nach vorn.
 * - Die Stapelreihenfolge kommt als reaktiver `style.zIndex` über
 *   `dialogProps` an den Dialog (siehe useDialogWindowDock).
 */

export interface DialogWindowResizeOptions {
  /** Mindestbreite des Fensters in px (Default 480). */
  minWidth?: number;
  /** Mindesthöhe des Fensters in px (Default 360). */
  minHeight?: number;
  /** Start-Breite als CSS-Länge, z. B. "min(1240px, 72vw)". */
  defaultWidth?: string;
  /** Start-Höhe als CSS-Länge, z. B. "min(900px, 76vh)". */
  defaultHeight?: string;
  /**
   * Start-Position: "center" (Default, Vuetify-Zentrierung) oder
   * "bottom-right" für ein kompaktes Seitenfenster, das den Seiteninhalt
   * nicht verdeckt (Kundenfeedback 30.08.26 zum Unterhaltungs-Fenster).
   */
  defaultPlacement?: "center" | "bottom-right";
}

export interface DialogWindowOptions {
  /** Eindeutige ID, z. B. "werte-edit". */
  id: string;
  /** Unübersetzter Titel für den Dock-Chip. */
  title: string;
  /** Lucide-Icon für den Dock-Chip. */
  icon?: Component;
  /** Offen-Zustand des Dialogs (i. d. R. `toRef(props, "modelValue")`). */
  isOpen: Ref<boolean>;
  /**
   * Aktiviert freies Skalieren über Resize-Handles sowie Maximieren/
   * Wiederherstellen. Additiv: bestehende Nutzer ohne diese Option
   * verhalten sich exakt wie bisher (nur Drag + Minimieren).
   */
  resizable?: boolean | DialogWindowResizeOptions;
  /**
   * Nicht-modales Arbeitsfenster: auch als einziges Fenster ohne Scrim,
   * Scroll-Sperre und Fokus-Falle, die Seite dahinter bleibt voll bedienbar.
   * Zusammen mit `persistent` am `v-dialog` bleibt es stehen, bis es
   * ausdrücklich geschlossen wird (Kundenfeedback 11.10.26, Wiedervorlagen).
   */
  modeless?: boolean;
}

/** Richtungen der Resize-Handles (Kanten + Ecken). */
export type DialogWindowResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** Mindestens sichtbarer horizontaler Anteil des Fensters in px. */
const MIN_VISIBLE_X = 120;
/** Oberer Mindestabstand des Fensters zum Viewport-Rand in px. */
const TOP_MARGIN = 8;
/** Der Header muss mindestens so viele px über dem unteren Rand bleiben. */
const HANDLE_SAFE_BOTTOM = 56;
/** Dauer der Minimieren-/Wiederherstellen-Animation in ms (CSS muss passen). */
const ANIMATION_MS = 340;
/** Zielpunkt der Minimieren-Animation (Dock-Ecke unten links). */
const DOCK_TARGET = { x: 96, y: 40 };
/** Rand zum Viewport im maximierten Zustand bzw. als Resize-Obergrenze. */
const MAXIMIZE_MARGIN = 8;
/** Abstand zum Viewport-Rand bei defaultPlacement "bottom-right". */
const PLACEMENT_MARGIN = 16;
/** Default-Mindestgröße für skalierbare Fenster. */
const DEFAULT_MIN_WIDTH = 480;
const DEFAULT_MIN_HEIGHT = 360;

/** Fallback, falls Vuetify (noch) keinen z-Index vergeben hat. */
const FALLBACK_Z = 2400;

const CLASS_CONTENT = "dialog-window";
const CLASS_DRAGGING = "dialog-window--dragging";
const CLASS_ANIMATING = "dialog-window--animating";
const CLASS_REPOSITIONING = "dialog-window--repositioning";
const CLASS_HIDDEN = "dialog-window--hidden";

const NO_DRAG_SELECTOR = "button, a, input, textarea, select, label, [data-no-drag]";

/**
 * Gibt ein Fenster Scrim, Scroll-Sperre und Fokus-Falle frei? Ja, wenn es
 * nicht-modal oder minimiert ist oder mehrere Fenster gleichzeitig sichtbar
 * sind (Fenstermodus).
 */
export function isPassiveWindow(state: { modeless: boolean; minimized: boolean; visibleCount: number }): boolean {
  return state.modeless || state.minimized || state.visibleCount > 1;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useDialogWindow(options: DialogWindowOptions) {
  const isMinimized = ref(false);
  const isMaximized = ref(false);
  const offset = reactive({ x: 0, y: 0 });

  // Resize-Konfiguration (nur aktiv, wenn `resizable` gesetzt ist).
  const isResizable = !!options.resizable;
  const resizeConfig: DialogWindowResizeOptions =
    typeof options.resizable === "object" ? options.resizable : {};
  const minWidth = resizeConfig.minWidth ?? DEFAULT_MIN_WIDTH;
  const minHeight = resizeConfig.minHeight ?? DEFAULT_MIN_HEIGHT;

  let cardEl: HTMLElement | null = null;
  let rafId: number | null = null;
  let animationTimer: number | null = null;
  let animationToken = 0;

  // Resize-Zustand (nur während eines aktiven Resize belegt)
  let resizeState: {
    dir: DialogWindowResizeDir;
    startX: number;
    startY: number;
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  } | null = null;
  let resizeHandleEl: HTMLElement | null = null;
  let resizeRafId: number | null = null;
  let resizePending: { x: number; y: number } | null = null;

  // Gemerkte Geometrie für Wiederherstellen nach Maximieren.
  let preMaximize: { x: number; y: number; width: string; height: string } | null = null;

  // Drag-Zustand (nur während eines aktiven Drags belegt)
  let dragBase: { left: number; top: number; width: number; height: number } | null = null;
  let dragStart = { pointerX: 0, pointerY: 0, offsetX: 0, offsetY: 0 };
  let pending = { x: 0, y: 0 };
  let activeHandle: HTMLElement | null = null;

  // Element mit aktivem Raise-Listener (Pointerdown holt das Fenster nach vorn)
  let raiseTarget: HTMLElement | null = null;

  function setCardEl(el: Element | ComponentPublicInstance | null): void {
    if (!el) {
      cardEl = null;
    } else if (el instanceof Element) {
      cardEl = el as HTMLElement;
    } else {
      cardEl = (el.$el as HTMLElement) ?? null;
    }
    attachRaiseListener();
  }

  function getContentEl(): HTMLElement | null {
    return cardEl?.closest<HTMLElement>(".v-overlay__content") ?? null;
  }

  function getOverlayEl(): HTMLElement | null {
    return cardEl?.closest<HTMLElement>(".v-overlay") ?? null;
  }

  function onContentPointerDown(): void {
    raiseDockWindow(options.id);
  }

  /**
   * Misst einmal je Öffnen den von Vuetify vergebenen z-Index des Overlays.
   * Er bildet die Untergrenze für die Stapelreihenfolge; danach rendert der
   * Dialog den Wert aus dem Dock-Store (siehe `dialogProps`).
   */
  function captureBaseZ(): void {
    if (!needsDockWindowBaseZ(options.id)) return;
    const overlay = getOverlayEl();
    if (!overlay) return;
    const z = Number.parseInt(getComputedStyle(overlay).zIndex, 10);
    setDockWindowBaseZ(options.id, Number.isFinite(z) ? z : FALLBACK_Z);
  }

  function attachRaiseListener(): void {
    const content = getContentEl();
    if (content) captureBaseZ();
    if (!content || content === raiseTarget) return;
    detachRaiseListener();
    raiseTarget = content;
    content.addEventListener("pointerdown", onContentPointerDown, true);
    applyInitialSize(content);
    // Sobald der Overlay-Inhalt steht, Stapelreihenfolge anwenden.
    raiseDockWindow(options.id);
  }

  /**
   * Wendet für skalierbare Fenster die konfigurierte Startgröße auf den
   * Overlay-Inhalt an (einmalig pro Öffnen; Resize überschreibt mit px).
   */
  function applyInitialSize(content: HTMLElement): void {
    if (!isResizable || content.dataset.dwSized) return;
    content.dataset.dwSized = "1";
    if (resizeConfig.defaultWidth) content.style.width = resizeConfig.defaultWidth;
    if (resizeConfig.defaultHeight) content.style.height = resizeConfig.defaultHeight;
    content.style.maxWidth = `calc(100vw - ${2 * MAXIMIZE_MARGIN}px)`;
    content.style.maxHeight = `calc(100vh - ${2 * MAXIMIZE_MARGIN}px)`;
    if (resizeConfig.defaultPlacement === "bottom-right") {
      // Nach dem Layout messen und den zentrierten Overlay-Inhalt per
      // Transform in die rechte untere Ecke verschieben (Compositor-only).
      requestAnimationFrame(() => {
        if (!options.isOpen.value || isMinimized.value || isMaximized.value) return;
        if (offset.x !== 0 || offset.y !== 0) return; // Nutzer hat schon verschoben
        // Layout-Box statt getBoundingClientRect(): Während Vuetifys
        // Öffnen-Transition ist der Inhalt noch skaliert, das Fenster landete
        // sonst teils jenseits des rechten/unteren Rands.
        const right = content.offsetLeft + content.offsetWidth;
        const bottom = content.offsetTop + content.offsetHeight;
        offset.x = window.innerWidth - right - PLACEMENT_MARGIN;
        offset.y = window.innerHeight - bottom - PLACEMENT_MARGIN;
        applyOffset(content, offset.x, offset.y);
      });
    }
  }

  /** Setzt alle vom Resize/Maximize gesetzten Inline-Größen zurück. */
  function clearSizeStyles(content: HTMLElement): void {
    delete content.dataset.dwSized;
    content.style.width = "";
    content.style.height = "";
    content.style.maxWidth = "";
    content.style.maxHeight = "";
    content.style.margin = "";
  }

  function detachRaiseListener(): void {
    if (!raiseTarget) return;
    raiseTarget.removeEventListener("pointerdown", onContentPointerDown, true);
    raiseTarget = null;
  }

  function applyOffset(el: HTMLElement, x: number, y: number): void {
    el.style.transform = x === 0 && y === 0 ? "" : `translate3d(${x}px, ${y}px, 0)`;
  }

  function clampOffset(x: number, y: number): { x: number; y: number } {
    if (!dragBase) return { x, y };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minX = MIN_VISIBLE_X - (dragBase.left + dragBase.width);
    const maxX = vw - MIN_VISIBLE_X - dragBase.left;
    const minY = TOP_MARGIN - dragBase.top;
    const maxY = vh - HANDLE_SAFE_BOTTOM - dragBase.top;
    return {
      x: Math.min(Math.max(x, minX), maxX),
      y: Math.min(Math.max(y, minY), maxY),
    };
  }

  function scheduleApply(): void {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const content = getContentEl();
      if (!content) return;
      offset.x = pending.x;
      offset.y = pending.y;
      applyOffset(content, offset.x, offset.y);
    });
  }

  function onPointerMove(e: PointerEvent): void {
    const raw = {
      x: dragStart.offsetX + (e.clientX - dragStart.pointerX),
      y: dragStart.offsetY + (e.clientY - dragStart.pointerY),
    };
    pending = clampOffset(raw.x, raw.y);
    scheduleApply();
  }

  function endDrag(): void {
    if (!activeHandle) return;
    activeHandle.removeEventListener("pointermove", onPointerMove);
    activeHandle.removeEventListener("pointerup", endDrag);
    activeHandle.removeEventListener("pointercancel", endDrag);
    activeHandle = null;
    dragBase = null;
    getContentEl()?.classList.remove(CLASS_DRAGGING);
  }

  function onHandlePointerDown(e: PointerEvent): void {
    if (isMinimized.value || isMaximized.value || e.button !== 0) return;
    if ((e.target as HTMLElement | null)?.closest(NO_DRAG_SELECTOR)) return;
    const content = getContentEl();
    const handle = e.currentTarget as HTMLElement | null;
    if (!content || !handle) return;

    clearAnimationClasses(content);
    const rect = content.getBoundingClientRect();
    dragBase = {
      left: rect.left - offset.x,
      top: rect.top - offset.y,
      width: rect.width,
      height: rect.height,
    };
    dragStart = { pointerX: e.clientX, pointerY: e.clientY, offsetX: offset.x, offsetY: offset.y };
    pending = { x: offset.x, y: offset.y };

    content.classList.add(CLASS_DRAGGING);
    activeHandle = handle;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* Pointer bereits weg (z. B. Test-Umgebung) — Drag läuft über Listener weiter. */
    }
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
    e.preventDefault();
  }

  // ── Resize (nur bei `resizable: true`) ────────────────────────────────
  //
  // Der konsumierende Dialog rendert unsichtbare Handle-Elemente an Kanten
  // und Ecken und bindet dort `onResizePointerDown($event, dir)`. Während
  // des Resize werden Breite/Höhe px-genau gesetzt; die gegenüberliegende
  // Kante bleibt fix, indem der zentrierte Overlay-Inhalt per Transform
  // um die halbe Delta verschoben wird (Compositor-freundlich, rAF-gedrosselt).

  function applyResize(): void {
    resizeRafId = null;
    const content = getContentEl();
    if (!content || !resizeState || !resizePending) return;
    const s = resizeState;
    const dx = resizePending.x - s.startX;
    const dy = resizePending.y - s.startY;
    const maxW = window.innerWidth - 2 * MAXIMIZE_MARGIN;
    const maxH = window.innerHeight - 2 * MAXIMIZE_MARGIN;
    let w = s.width;
    let h = s.height;
    if (s.dir.includes("e")) w = s.width + dx;
    else if (s.dir.includes("w")) w = s.width - dx;
    if (s.dir.includes("s")) h = s.height + dy;
    else if (s.dir.includes("n")) h = s.height - dy;
    w = Math.min(Math.max(w, minWidth), maxW);
    h = Math.min(Math.max(h, minHeight), maxH);
    content.style.width = `${w}px`;
    content.style.height = `${h}px`;
    // Gegenüberliegende Kante fixieren (Overlay-Inhalt ist zentriert).
    offset.x = s.offsetX + (s.dir.includes("e") ? (w - s.width) / 2 : s.dir.includes("w") ? (s.width - w) / 2 : 0);
    offset.y = s.offsetY + (s.dir.includes("s") ? (h - s.height) / 2 : s.dir.includes("n") ? (s.height - h) / 2 : 0);
    applyOffset(content, offset.x, offset.y);
  }

  function onResizeMove(e: PointerEvent): void {
    resizePending = { x: e.clientX, y: e.clientY };
    if (resizeRafId !== null) return;
    resizeRafId = requestAnimationFrame(applyResize);
  }

  function endResize(): void {
    if (!resizeHandleEl) return;
    resizeHandleEl.removeEventListener("pointermove", onResizeMove);
    resizeHandleEl.removeEventListener("pointerup", endResize);
    resizeHandleEl.removeEventListener("pointercancel", endResize);
    resizeHandleEl = null;
    resizeState = null;
    resizePending = null;
    if (resizeRafId !== null) {
      cancelAnimationFrame(resizeRafId);
      resizeRafId = null;
    }
    getContentEl()?.classList.remove(CLASS_DRAGGING);
  }

  /** Startet das freie Skalieren über ein Kanten-/Ecken-Handle. */
  function onResizePointerDown(e: PointerEvent, dir: DialogWindowResizeDir): void {
    if (!isResizable || isMinimized.value || isMaximized.value || e.button !== 0) return;
    const content = getContentEl();
    const handle = e.currentTarget as HTMLElement | null;
    if (!content || !handle) return;

    clearAnimationClasses(content);
    const rect = content.getBoundingClientRect();
    resizeState = {
      dir,
      startX: e.clientX,
      startY: e.clientY,
      width: rect.width,
      height: rect.height,
      offsetX: offset.x,
      offsetY: offset.y,
    };
    content.classList.add(CLASS_DRAGGING);
    resizeHandleEl = handle;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* Pointer bereits weg (z. B. Test-Umgebung) — Resize läuft über Listener weiter. */
    }
    handle.addEventListener("pointermove", onResizeMove);
    handle.addEventListener("pointerup", endResize);
    handle.addEventListener("pointercancel", endResize);
    e.preventDefault();
    e.stopPropagation();
  }

  // ── Maximieren / Wiederherstellen ─────────────────────────────────────

  /** Maximiert das Fenster auf den Viewport (mit schmalem Rand). */
  function maximize(): void {
    if (isMaximized.value || isMinimized.value || !options.isOpen.value) return;
    const content = getContentEl();
    if (!content) return;
    endDrag();
    endResize();
    clearAnimationClasses(content);
    preMaximize = {
      x: offset.x,
      y: offset.y,
      width: content.style.width,
      height: content.style.height,
    };
    isMaximized.value = true;
    offset.x = 0;
    offset.y = 0;
    applyOffset(content, 0, 0);
    const size = `calc(100vw - ${2 * MAXIMIZE_MARGIN}px)`;
    const sizeH = `calc(100vh - ${2 * MAXIMIZE_MARGIN}px)`;
    content.style.width = size;
    content.style.maxWidth = size;
    content.style.height = sizeH;
    content.style.maxHeight = sizeH;
    content.style.margin = `${MAXIMIZE_MARGIN}px`;
  }

  /** Stellt Größe und Position von vor dem Maximieren wieder her. */
  function unmaximize(): void {
    if (!isMaximized.value) return;
    const content = getContentEl();
    isMaximized.value = false;
    if (!content) return;
    content.style.width = preMaximize?.width ?? "";
    content.style.height = preMaximize?.height ?? "";
    content.style.margin = "";
    content.style.maxWidth = `calc(100vw - ${2 * MAXIMIZE_MARGIN}px)`;
    content.style.maxHeight = `calc(100vh - ${2 * MAXIMIZE_MARGIN}px)`;
    offset.x = preMaximize?.x ?? 0;
    offset.y = preMaximize?.y ?? 0;
    applyOffset(content, offset.x, offset.y);
    preMaximize = null;
  }

  function toggleMaximize(): void {
    if (isMaximized.value) unmaximize();
    else maximize();
  }

  function clearAnimationClasses(content: HTMLElement): void {
    content.classList.remove(CLASS_ANIMATING, CLASS_REPOSITIONING);
    if (animationTimer !== null) {
      window.clearTimeout(animationTimer);
      animationTimer = null;
    }
  }

  /**
   * Doppelklick auf den Header: bei skalierbaren Fenstern Maximieren/
   * Wiederherstellen (Outlook-Verhalten), sonst sanft zurück in die Mitte.
   */
  function onHandleDblClick(e: MouseEvent): void {
    if (isMinimized.value) return;
    if ((e.target as HTMLElement | null)?.closest(NO_DRAG_SELECTOR)) return;
    if (isResizable) {
      toggleMaximize();
      return;
    }
    const content = getContentEl();
    if (!content || (offset.x === 0 && offset.y === 0)) return;

    offset.x = 0;
    offset.y = 0;
    if (prefersReducedMotion()) {
      applyOffset(content, 0, 0);
      return;
    }
    const token = ++animationToken;
    content.classList.add(CLASS_REPOSITIONING);
    applyOffset(content, 0, 0);
    animationTimer = window.setTimeout(() => {
      if (token !== animationToken) return;
      content.classList.remove(CLASS_REPOSITIONING);
      animationTimer = null;
    }, ANIMATION_MS);
  }

  /** Minimiert das Fenster in die Dock-Leiste; Formularzustand bleibt erhalten. */
  function minimize(): void {
    if (isMinimized.value || !options.isOpen.value) return;
    const content = getContentEl();
    if (!content) return;

    endDrag();
    endResize();
    clearAnimationClasses(content);

    // Scrim, Scroll-Sperre und Fokus-Falle sofort freigeben (via dialogProps).
    isMinimized.value = true;
    setDockWindowMinimized(options.id, true);

    const rect = content.getBoundingClientRect();
    const targetX = offset.x + (DOCK_TARGET.x - (rect.left + rect.width / 2));
    const targetY = offset.y + (window.innerHeight - DOCK_TARGET.y - (rect.top + rect.height / 2));
    const targetTransform = `translate3d(${targetX}px, ${targetY}px, 0) scale(0.06)`;
    const token = ++animationToken;

    if (prefersReducedMotion()) {
      content.style.transform = targetTransform;
      content.style.opacity = "0";
      content.classList.add(CLASS_HIDDEN);
      return;
    }

    content.classList.add(CLASS_ANIMATING);
    requestAnimationFrame(() => {
      if (token !== animationToken) return;
      content.style.transform = targetTransform;
      content.style.opacity = "0";
    });
    animationTimer = window.setTimeout(() => {
      if (token !== animationToken) return;
      content.classList.add(CLASS_HIDDEN);
      content.classList.remove(CLASS_ANIMATING);
      animationTimer = null;
    }, ANIMATION_MS);
  }

  /** Holt das Fenster aus der Dock-Leiste an seine letzte Position zurück. */
  function restore(): void {
    if (!isMinimized.value) return;
    setDockWindowMinimized(options.id, false);
    const content = getContentEl();
    isMinimized.value = false;
    raiseDockWindow(options.id);
    if (!content) return;

    const token = ++animationToken;
    content.classList.remove(CLASS_HIDDEN);

    if (prefersReducedMotion()) {
      applyOffset(content, offset.x, offset.y);
      content.style.opacity = "";
      return;
    }

    // Reflow erzwingen, damit die Rück-Transition vom Dock-Punkt aus startet.
    void content.getBoundingClientRect();
    content.classList.add(CLASS_ANIMATING);
    requestAnimationFrame(() => {
      if (token !== animationToken) return;
      applyOffset(content, offset.x, offset.y);
      content.style.opacity = "1";
    });
    animationTimer = window.setTimeout(() => {
      if (token !== animationToken) return;
      content.classList.remove(CLASS_ANIMATING);
      content.style.opacity = "";
      animationTimer = null;
    }, ANIMATION_MS);
  }

  /** Nach Fenster-Resize die Position neu klemmen, damit nichts verloren geht. */
  function onWindowResize(): void {
    if (!options.isOpen.value || isMinimized.value || isMaximized.value) return;
    if (offset.x === 0 && offset.y === 0) return;
    const content = getContentEl();
    if (!content) return;
    const rect = content.getBoundingClientRect();
    dragBase = {
      left: rect.left - offset.x,
      top: rect.top - offset.y,
      width: rect.width,
      height: rect.height,
    };
    const clamped = clampOffset(offset.x, offset.y);
    dragBase = null;
    offset.x = clamped.x;
    offset.y = clamped.y;
    applyOffset(content, offset.x, offset.y);
  }

  function resetState(): void {
    animationToken++;
    endDrag();
    endResize();
    detachRaiseListener();
    unregisterDockWindow(options.id);
    isMinimized.value = false;
    isMaximized.value = false;
    preMaximize = null;
    offset.x = 0;
    offset.y = 0;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (animationTimer !== null) {
      window.clearTimeout(animationTimer);
      animationTimer = null;
    }
    const content = getContentEl();
    if (content) {
      content.classList.remove(CLASS_DRAGGING, CLASS_ANIMATING, CLASS_REPOSITIONING, CLASS_HIDDEN);
      content.style.transform = "";
      content.style.opacity = "";
      clearSizeStyles(content);
    }
  }

  watch(
    options.isOpen,
    (open) => {
      if (open) {
        registerDockWindow({
          id: options.id,
          title: options.title,
          icon: options.icon ?? null,
          minimized: false,
          restore,
          minimize,
        });
        // Nach dem Mount des Overlay-Inhalts nach vorn holen und die
        // Startgröße anwenden. `setCardEl` kann feuern, bevor der Overlay-
        // Inhalt im DOM steht — dann fand `applyInitialSize` kein Ziel und
        // das Fenster öffnete in Vuetify-Standardbreite, um erst beim
        // nächsten Re-Render (z. B. Fallauswahl) auf die konfigurierte Breite
        // zu springen (Inhaber-Feedback 15.08.26 I.2, „verschiebt sich nach
        // rechts“). Deshalb hier erneut anbinden, notfalls im nächsten Frame.
        nextTick(() => {
          attachRaiseListener();
          if (!raiseTarget) requestAnimationFrame(() => attachRaiseListener());
          raiseDockWindow(options.id);
        });
      } else {
        resetState();
      }
    },
    { immediate: true },
  );

  window.addEventListener("resize", onWindowResize);
  onScopeDispose(() => {
    window.removeEventListener("resize", onWindowResize);
    resetState();
  });

  /** Bindings für den `v-dialog` (via `v-bind="win.dialogProps"`). */
  const dialogProps = computed(() => {
    // Fenstermodus: sobald mehrere Fenster sichtbar sind, geben alle Fenster
    // Scrim, Scroll-Sperre und Fokus-Falle frei, damit sie parallel bedien-
    // und anordenbar sind. Ein einzelnes Fenster verhält sich wie bisher.
    const passive = isPassiveWindow({
      modeless: !!options.modeless,
      minimized: isMinimized.value,
      visibleCount: visibleWindowCount.value,
    });
    // Stapelreihenfolge: Vue rendert den z-Index selbst, damit Vuetify ihn
    // beim nächsten Neuzeichnen nicht wieder überschreibt (Kundenfeedback
    // 26.10.2026, I – wiederhergestelltes Fenster blieb hinten).
    const zIndex = dockWindowZIndex(options.id);
    return {
      ...(zIndex === null ? {} : { style: { zIndex } }),
      scrim: !passive,
      "scroll-strategy": (passive ? "none" : "block") as "none" | "block",
      "retain-focus": !passive,
      "no-click-animation": passive,
      "content-class": CLASS_CONTENT,
      // Ohne Fokus-Falle ist die Seite dahinter bedienbar – Screenreader
      // dürfen sie dann nicht als inert behandeln.
      "aria-modal": (passive ? "false" : "true") as "false" | "true",
    };
  });

  /**
   * Holt ein bereits offenes Fenster nach vorn – minimiert wird es
   * wiederhergestellt, sonst über die anderen Fenster gehoben.
   */
  function bringToFront(): void {
    if (isMinimized.value) restore();
    else raiseDockWindow(options.id);
  }

  return reactive({
    isMinimized,
    isMaximized,
    dialogProps,
    setCardEl,
    onHandlePointerDown,
    onHandleDblClick,
    onResizePointerDown,
    minimize,
    restore,
    maximize,
    unmaximize,
    toggleMaximize,
    bringToFront,
  });
}
